import React, { useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { Input } from '../ui/input';

/**
 * Персональные лимиты — исключение, а не обычная настройка.
 *
 * До квотного движка админ задавал клиенту лимиты только числом. Теперь лимит
 * задаёт тариф, а персональное число ПЕРЕБИВАЕТ его (`override ?? plan`) —
 * то есть введённая здесь цифра молча отменяет выданную подписку. Поэтому поля
 * свёрнуты и подписаны, а не стоят в форме наравне с остальными.
 *
 * Свёрнуты они только когда пусты: уже заданный override обязан быть виден
 * сразу, иначе ровно эта ловушка и останется незамеченной.
 */
export default function PersonalQuotaFields({
  audio, summary, hourly = '', onAudioChange, onSummaryChange, onHourlyChange, defaults, subscription,
}) {
  const isSet = (v) => v !== '' && v != null;
  const hasValue = isSet(audio) || isSet(summary) || isSet(hourly);
  // Раскрытость выводится из значения, а не фиксируется на монтировании:
  // EditUserDialog заполняет квоты в useEffect, то есть уже ПОСЛЕ первого рендера
  // детей. С `useState(hasValue)` блок оставался бы свёрнутым именно там, где
  // override задан — ровно та ловушка, против которой всё и написано.
  // toggled !== null означает, что админ переключил блок руками — его выбор старше.
  const [toggled, setToggled] = useState(null);
  const open = toggled === null ? hasValue : toggled;
  const setOpen = (next) => setToggled(typeof next === 'function' ? next(open) : next);

  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="rounded-chip bg-paper-bg">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-paper-ink-2 hover:bg-paper-panel"
      >
        <Chevron className="w-4 h-4 text-paper-hint" />
        <span>Персональный лимит (исключение)</span>
        {hasValue && (
          <span className="ml-auto text-[10px] rounded px-1.5 py-0.5 bg-paper-panel text-paper-warning">
            задан
          </span>
        )}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          <div className="flex gap-2 bg-paper-panel rounded p-2.5">
            <AlertTriangle className="w-4 h-4 text-paper-warning shrink-0 mt-0.5" />
            <p className="text-[11px] text-paper-warning leading-snug">
              Персональный лимит <b>перебивает тариф</b>: заданное число действует вместо лимита
              подписки{subscription ? '' : ' и вместо «Оплаты по факту»'}. Пустое поле — работает тариф.
              {subscription && (
                <> Сейчас по тарифу: {subscription.minutes_limit} мин и {subscription.llm_limit} саммари.</>
              )}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label htmlFor="quota-audio" className="text-xs text-paper-ink-3 block mb-1">Аудио (мин)</label>
              <Input
                id="quota-audio"
                type="number" value={audio} onChange={(e) => onAudioChange(e.target.value)}
                placeholder={defaults?.audio != null ? `${defaults.audio} (дефолт роли)` : 'Безлимит'}
              />
            </div>
            <div>
              <label htmlFor="quota-summary" className="text-xs text-paper-ink-3 block mb-1">Суммаризаций</label>
              <Input
                id="quota-summary"
                type="number" value={summary} onChange={(e) => onSummaryChange(e.target.value)}
                placeholder={defaults?.summary != null ? `${defaults.summary} (дефолт роли)` : 'Безлимит'}
              />
            </div>
            <div>
              {/* Часовой рубеж — единственный, который действует и на service:
                  02.09.2026 служебная учётка залпами протоколов держала очередь
                  суммаризатора для всех. Дефолт роли задаёт bit-metrics. */}
              <label htmlFor="quota-hourly" className="text-xs text-paper-ink-3 block mb-1">В час</label>
              <Input
                id="quota-hourly"
                type="number" value={hourly} onChange={(e) => onHourlyChange && onHourlyChange(e.target.value)}
                placeholder={defaults?.hourly != null ? `${defaults.hourly} (дефолт роли)` : 'Безлимит'}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
